import { useState } from "react";
import { Loader2, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { readEdgeError } from "@/lib/edge-error";

interface PackCheck {
  packId: string;
  externalId: string;
  resolved: string | null;
  error?: string;
}

interface EnvCheck {
  env: "sandbox" | "live";
  reachable: boolean;
  status?: number;
  error?: string;
  packs: PackCheck[];
}

interface PaddleOrder {
  id: string;
  user_id: string;
  paddle_transaction_id: string;
  pack_id: string;
  amount_cents: number;
  currency: string;
  credits: number;
  status: string;
  environment: string;
  created_at: string;
}

interface StatusPayload {
  ok: boolean;
  checkedAt: string;
  secrets: {
    sandboxKey: boolean;
    liveKey: boolean;
    sandboxWebhookSecret: boolean;
    liveWebhookSecret: boolean;
  };
  sandbox: EnvCheck;
  live: EnvCheck;
  orders: PaddleOrder[];
  counts: { total: number; sandbox: number; live: number; completed: number; created: number };
}

const fmtMoney = (cents: number, currency: string) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: currency || "USD" });

function StatusDot({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
  ) : (
    <XCircle className="h-4 w-4 text-destructive" />
  );
}

function EnvBlock({ check }: { check: EnvCheck }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusDot ok={check.reachable} />
          <span className="font-display text-xs font-semibold uppercase tracking-[0.18em]">
            {check.env}
          </span>
          {check.status ? (
            <span className="text-xs text-muted-foreground">HTTP {check.status}</span>
          ) : null}
        </div>
        <span className={check.reachable ? "text-xs text-emerald-600" : "text-xs text-destructive"}>
          {check.reachable ? "Connected" : "Failed"}
        </span>
      </div>
      {check.error ? (
        <p className="mb-2 break-words rounded bg-destructive/10 p-2 text-[11px] text-destructive">
          {check.error}
        </p>
      ) : null}
      <div className="space-y-1">
        {check.packs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No catalog check (env unreachable).</p>
        ) : (
          check.packs.map((p) => (
            <div key={p.packId} className="flex items-center justify-between gap-2 text-xs">
              <span className="font-mono text-muted-foreground">{p.externalId}</span>
              <span className="flex items-center gap-1.5">
                {p.resolved ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="font-mono text-[10px] text-muted-foreground">{p.resolved.slice(0, 14)}…</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-3.5 w-3.5 text-destructive" />
                    <span className="text-destructive">{p.error ?? "missing"}</span>
                  </>
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function PaddleHealthCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StatusPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data: res, error } = await supabase.functions.invoke("admin-paddle-status", { body: {} });
      if (error) {
        setErr((await readEdgeError(error)).message);
        return;
      }
      setData(res as StatusPayload);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">Paddle health</CardTitle>
        <Button size="sm" variant="outline" onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {data ? "Re-run check" : "Run check"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {err ? (
          <p className="rounded bg-destructive/10 p-2 text-xs text-destructive">{err}</p>
        ) : null}

        {!data && !loading ? (
          <p className="text-sm text-muted-foreground">
            Click <em>Run check</em> to ping the Paddle gateway (sandbox + live), verify the credit
            pack catalog is in sync, and list the most recent orders.
          </p>
        ) : null}

        {data ? (
          <>
            {/* Secrets present */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <SecretChip label="Sandbox key" ok={data.secrets.sandboxKey} />
              <SecretChip label="Live key" ok={data.secrets.liveKey} />
              <SecretChip label="Sandbox webhook" ok={data.secrets.sandboxWebhookSecret} />
              <SecretChip label="Live webhook" ok={data.secrets.liveWebhookSecret} />
            </div>

            {/* Env reachability + catalog */}
            <div className="grid gap-3 md:grid-cols-2">
              <EnvBlock check={data.sandbox} />
              <EnvBlock check={data.live} />
            </div>

            {/* Order counters */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <CountChip label="Orders 30d" value={data.counts.total} />
              <CountChip label="Sandbox" value={data.counts.sandbox} />
              <CountChip label="Live" value={data.counts.live} />
              <CountChip label="Completed" value={data.counts.completed} accent="emerald" />
              <CountChip label="Pending" value={data.counts.created} accent="muted" />
            </div>

            {/* Orders list */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Recent orders
              </h4>
              {data.orders.length === 0 ? (
                <p className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground">
                  No Paddle orders in the last 30 days. Click <em>Test Paddle checkout</em> below to
                  create a sandbox transaction.
                </p>
              ) : (
                <div className="space-y-1 text-xs">
                  {data.orders.map((o) => (
                    <div
                      key={o.id}
                      className="flex flex-wrap items-center justify-between gap-2 border-b pb-1 last:border-0"
                    >
                      <span className="font-mono text-muted-foreground">
                        {new Date(o.created_at).toLocaleString()}
                      </span>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">
                        {o.environment}
                      </span>
                      <span>{o.pack_id}</span>
                      <span className="font-medium tabular-nums">
                        {fmtMoney(o.amount_cents, o.currency)} · {o.credits} cr
                      </span>
                      <span
                        className={
                          o.status === "completed"
                            ? "text-emerald-600"
                            : o.status === "created"
                            ? "text-muted-foreground"
                            : "text-destructive"
                        }
                      >
                        {o.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground">
              Last checked: {new Date(data.checkedAt).toLocaleString()}
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SecretChip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center gap-1.5 rounded border px-2 py-1.5 text-xs">
      <StatusDot ok={ok} />
      <span className="truncate">{label}</span>
    </div>
  );
}

function CountChip({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "emerald" | "muted";
}) {
  return (
    <div className="rounded border p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={
          "mt-0.5 text-lg font-semibold tabular-nums " +
          (accent === "emerald"
            ? "text-emerald-600"
            : accent === "muted"
            ? "text-muted-foreground"
            : "")
        }
      >
        {value}
      </div>
    </div>
  );
}
